/**
 * Dashboard.tsx — customer home, Glass + Aurora edition.
 *
 * Two layered scenes:
 *   1. Aurora canvas — large soft radial gradients (navy + orange) animating
 *      slowly across the background. Pure CSS keyframes for performance.
 *   2. Glass UI — frosted cards (backdrop-blur) with subtle borders + inner
 *      glow on hover. Real data is the hero: large luminous numbers,
 *      inline SVG sparkline, pulse dots on live items.
 *
 * Data: one round-trip to /api/dashboard/summary.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashShell from '../components/dash/DashShell';

interface HostingItem {
  id: string;
  primary_domain: string;
  status: string;
  php_version: string;
  ssl_issued_at: string | null;
  created_at: string;
}
interface VpsItem {
  id: string;
  hostname: string;
  ip_address: string | null;
  status: string;
  plan_name: string | null;
  created_at: string;
}
interface MailItem {
  domain: string;
  ses_identity_status: string;
  mail_ct_domain_id: number | null;
  webmail_vhost_installed_at: string | null;
  created_at: string;
}
interface DomainItem {
  id: string;
  domain_name: string;
  expiration_date: string | null;
  auto_renew: boolean;
  status: string;
  days_left: number | null;
}
interface ActivityItem {
  type: 'order' | 'ticket' | 'invoice';
  ref_id: string;
  at: string;
  summary: string;
  status: string;
}

interface Summary {
  services: {
    hosting: { total: number; active: number; suspended: number; items: HostingItem[] };
    vps: { total: number; running: number; stopped: number; items: VpsItem[] };
    mail: { domains_total: number; mailboxes_total: number; items: MailItem[] };
    domains: { total: number; expiring_soon: number; expired: number; items: DomainItem[] };
  };
  billing: {
    active_subscriptions: number;
    monthly_total_usd: number;
    next_charge_at: string | null;
    currency: string;
    spend_sparkline?: number[];
  };
  activity: ActivityItem[];
  support: { open_tickets: number; last_activity_at: string | null };
  customer: { id: string; first_name: string; email: string };
}

const apiBase = process.env.REACT_APP_API_URL || '';

const Dashboard: React.FC = () => {
  const { user, loading: authLoading } = useAuth() as any;
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const verifyMsg = searchParams.get('verified') === 'true';

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${apiBase}/api/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (j.success) setSummary(j.data);
      else setErr(j.error || 'Failed to load');
    } catch (e: any) { setErr(e.message || 'Network error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  if (authLoading) return <FullScreenSpinner />;
  if (!user) return <AccessDenied />;

  const greeting = `${greetingFor(new Date())}, ${user.firstName || 'there'}`;

  return (
    <DashShell
      title={greeting}
      subtitle="Everything you run, in one calm view."
      right={verifyMsg ? (
        <div className="glass-pill text-emerald-700 border-emerald-300/60">✓ Email verified</div>
      ) : undefined}
    >
        {err && (
          <div className="glass-card border-red-300/60 px-4 py-3 text-sm text-red-700 mb-6 fade-up">{err}</div>
        )}

        {/* Quick actions */}
        <section className="mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <QuickAction icon="🌐" label="Order Hosting"  to="/hosting" />
            <QuickAction icon="🚀" label="Order VPS"      to="/vps" />
            <QuickAction icon="🌍" label="Buy Domain"     to="/domains" />
            <QuickAction icon="💬" label="Open Ticket"    to="/dashboard/support" />
          </div>
        </section>

        {/* Snapshot strip — 4 stat tiles */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatTile
            delay={80}
            label="Monthly recurring"
            value={summary ? `$${summary.billing.monthly_total_usd.toFixed(2)}` : '…'}
            hint={summary?.billing.currency || 'USD'}
            sparkline={summary?.billing.spend_sparkline}
            accent="from-amber-400 via-orange-500 to-amber-300"
          />
          <StatTile
            delay={140}
            label="Active subscriptions"
            value={summary ? String(summary.billing.active_subscriptions) : '…'}
            hint="Stripe billing"
            accent="from-sky-400 via-cyan-400 to-sky-300"
          />
          <StatTile
            delay={200}
            label="Next charge"
            value={summary?.billing.next_charge_at ? fmtShortDate(summary.billing.next_charge_at) : '—'}
            hint={summary?.billing.next_charge_at ? daysUntil(summary.billing.next_charge_at) : 'no upcoming charge'}
            accent="from-violet-400 via-purple-400 to-violet-300"
          />
          <StatTile
            delay={260}
            label="Open tickets"
            value={summary ? String(summary.support.open_tickets) : '…'}
            hint={summary && summary.support.open_tickets > 0 ? 'needs attention' : 'all caught up'}
            link={summary && summary.support.open_tickets > 0 ? '/dashboard/support' : undefined}
            accent={summary && summary.support.open_tickets > 0
              ? 'from-rose-400 via-amber-400 to-rose-300'
              : 'from-emerald-400 via-teal-400 to-emerald-300'}
          />
        </section>

        {/* Domain expiry alert */}
        {summary && (summary.services.domains.expiring_soon > 0 || summary.services.domains.expired > 0) && (
          <section className="glass-card border-amber-300/70 p-5 mb-8 fade-up flex items-start gap-4" style={{ animationDelay: '300ms' }}>
            <div className="text-2xl">⚠︎</div>
            <div className="flex-1">
              <div className="font-medium text-amber-900">
                {summary.services.domains.expired > 0 && `${summary.services.domains.expired} expired`}
                {summary.services.domains.expired > 0 && summary.services.domains.expiring_soon > 0 && ' · '}
                {summary.services.domains.expiring_soon > 0 && `${summary.services.domains.expiring_soon} expiring in 30 days`}
              </div>
              <ul className="mt-2 text-sm text-amber-700 space-y-0.5">
                {summary.services.domains.items
                  .filter(d => d.days_left !== null && d.days_left <= 30)
                  .slice(0, 4)
                  .map(d => (
                    <li key={d.id} className="flex items-center gap-2">
                      <span className="inline-block w-1 h-1 rounded-full bg-amber-500" />
                      <strong className="font-medium">{d.domain_name}</strong>
                      <span>—</span>
                      <span>
                        {d.days_left! < 0
                          ? `expired ${Math.abs(d.days_left!)}d ago`
                          : `${d.days_left} day${d.days_left !== 1 ? 's' : ''} left`}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          </section>
        )}

        {loading && !summary ? (
          <SkeletonGrid />
        ) : !summary ? (
          <div className="text-center py-20 text-slate-400">No data available.</div>
        ) : (
          <>
            {/* Service health grid */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
              <ServiceCard
                delay={340}
                title="Web Hosting"
                glyph={IconHosting}
                accentClass="text-sky-700"
                accentChip="bg-sky-50"
                accentRing="ring-sky-200/70"
                total={summary.services.hosting.total}
                statusPills={[
                  { label: 'active', count: summary.services.hosting.active, tone: 'emerald' },
                  { label: 'suspended', count: summary.services.hosting.suspended, tone: 'rose' },
                ]}
                empty="No hosting accounts yet."
                cta={{ label: 'Open hosting', to: '/customer/hosting' }}
              >
                {summary.services.hosting.items.slice(0, 3).map((h, i) => (
                  <Row
                    key={h.id}
                    primary={h.primary_domain || 'unnamed'}
                    meta={[
                      { text: `PHP ${h.php_version}`, dim: true },
                      { text: h.status, tone: h.status === 'active' ? 'emerald' : 'rose' },
                    ]}
                    pulse={i === 0 && h.status === 'active'}
                    to={`/customer/hosting/${h.id}`}
                  />
                ))}
              </ServiceCard>

              <ServiceCard
                delay={400}
                title="VPS"
                glyph={IconVps}
                accentClass="text-violet-700"
                accentChip="bg-violet-50"
                accentRing="ring-violet-200/70"
                total={summary.services.vps.total}
                statusPills={[
                  { label: 'running', count: summary.services.vps.running, tone: 'emerald' },
                  { label: 'stopped', count: summary.services.vps.stopped, tone: 'slate' },
                ]}
                empty="No VPS instances yet."
                cta={{ label: 'Open VPS', to: '/customer/vps' }}
              >
                {summary.services.vps.items.slice(0, 3).map((v, i) => (
                  <Row
                    key={v.id}
                    primary={v.hostname}
                    meta={[
                      { text: v.ip_address || '—', mono: true, dim: true },
                      { text: v.status, tone: v.status === 'running' ? 'emerald' : 'slate' },
                    ]}
                    pulse={i === 0 && v.status === 'running'}
                  />
                ))}
              </ServiceCard>

              <ServiceCard
                delay={460}
                title="Mail"
                glyph={IconMail}
                accentClass="text-teal-700"
                accentChip="bg-teal-50"
                accentRing="ring-teal-200/70"
                total={summary.services.mail.domains_total}
                statusPills={[
                  { label: 'verified', count: summary.services.mail.items.filter(m => m.ses_identity_status === 'verified').length, tone: 'emerald' },
                  { label: 'verifying', count: summary.services.mail.items.filter(m => m.ses_identity_status === 'verifying' || m.ses_identity_status === 'pending').length, tone: 'amber' },
                ]}
                empty="Set up a mail domain from your hosting account."
                cta={{ label: 'Manage mail', to: '/email-dashboard' }}
              >
                {summary.services.mail.items.slice(0, 3).map((m, i) => (
                  <Row
                    key={m.domain}
                    primary={m.domain}
                    meta={[
                      m.webmail_vhost_installed_at ? { text: 'webmail ✓', tone: 'teal' as const } : null,
                      { text: m.ses_identity_status, tone: m.ses_identity_status === 'verified' ? 'emerald' as const : 'amber' as const },
                    ].filter(Boolean) as any}
                    pulse={i === 0 && m.ses_identity_status === 'verified'}
                  />
                ))}
              </ServiceCard>

              <ServiceCard
                delay={520}
                title="Domains"
                glyph={IconGlobe}
                accentClass="text-orange-700"
                accentChip="bg-orange-50"
                accentRing="ring-orange-200/70"
                total={summary.services.domains.total}
                statusPills={[
                  { label: 'active', count: summary.services.domains.total - summary.services.domains.expired, tone: 'emerald' },
                  ...(summary.services.domains.expiring_soon > 0 ? [{ label: 'expiring', count: summary.services.domains.expiring_soon, tone: 'amber' as const }] : []),
                  ...(summary.services.domains.expired > 0 ? [{ label: 'expired', count: summary.services.domains.expired, tone: 'rose' as const }] : []),
                ]}
                empty="Register a domain to get started."
                cta={{ label: 'Manage domains', to: '/dashboard/domains' }}
              >
                {summary.services.domains.items.slice(0, 3).map(d => (
                  <Row
                    key={d.id}
                    primary={d.domain_name}
                    meta={[
                      d.expiration_date
                        ? { text: `expires ${fmtShortDate(d.expiration_date)}`, dim: true, tone: d.days_left !== null && d.days_left < 30 ? 'amber' as const : undefined }
                        : { text: 'no expiry', dim: true },
                    ]}
                  />
                ))}
              </ServiceCard>
            </section>

            {/* Activity feed */}
            <section className="glass-card p-6 fade-up" style={{ animationDelay: '580ms' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.6)]" />
                  <h2 className="font-medium text-slate-900">Live activity</h2>
                </div>
                <Link to="/dashboard/orders" className="text-xs text-slate-500 hover:text-orange-600 transition">
                  View all →
                </Link>
              </div>
              {summary.activity.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">Quiet so far. Activity appears here once you place orders or open tickets.</p>
              ) : (
                <ul className="divide-y divide-slate-200/60">
                  {summary.activity.map((a, i) => (
                    <li key={`${a.type}-${a.ref_id}-${i}`} className="py-3 flex items-center gap-3 group">
                      <ActivityGlyph type={a.type} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{labelForActivity(a)}</div>
                        <div className="text-xs text-slate-500">{fmtRelative(a.at)}</div>
                      </div>
                      <ActivityStatus s={a.status} />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}

        {/* Footer note */}
        <p className="text-xs text-slate-400 text-center mt-12 fade-up" style={{ animationDelay: '640ms' }}>
          Built for makers · 12and22
        </p>
    </DashShell>
  );
};

// ============================================================================
// QuickAction — small icon + label tile linking to a key action
// ============================================================================

const QuickAction: React.FC<{ icon: string; label: string; to: string }> = ({ icon, label, to }) => (
  <Link
    to={to}
    className="group flex items-center gap-3 p-3 rounded-2xl bg-white/70 backdrop-blur-md border border-white/85 hover:border-orange-200 hover:shadow-md transition fade-up"
    style={{ animationDelay: '60ms' }}
  >
    <span className="text-xl flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100/80 group-hover:from-orange-100 group-hover:to-amber-100 transition">
      {icon}
    </span>
    <span className="text-sm font-medium text-slate-800 group-hover:text-orange-700 transition">{label}</span>
    <span className="ml-auto text-slate-300 group-hover:text-orange-500 transition">→</span>
  </Link>
);

// ============================================================================
// StatTile — large glass tile with optional inline sparkline
// ============================================================================

const StatTile: React.FC<{
  label: string;
  value: string;
  hint?: string;
  sparkline?: number[];
  link?: string;
  accent: string;
  delay?: number;
}> = ({ label, value, hint, sparkline, link, accent, delay }) => {
  const Inner = (
    <div className="glass-card p-5 h-full relative overflow-hidden group">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</div>
      <div className="text-3xl font-semibold text-slate-900 tracking-tight">{value}</div>
      {sparkline && sparkline.some(v => v > 0) && (
        <div className="mt-3 -mx-1">
          <Sparkline values={sparkline} />
        </div>
      )}
      {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
    </div>
  );
  return (
    <div className="fade-up" style={{ animationDelay: `${delay || 0}ms` }}>
      {link ? <Link to={link} className="block h-full">{Inner}</Link> : Inner}
    </div>
  );
};

const Sparkline: React.FC<{ values: number[] }> = ({ values }) => {
  const max = Math.max(...values, 1);
  const w = 100, h = 22, pad = 1;
  const step = (w - pad * 2) / Math.max(values.length - 1, 1);
  const points = values.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - (v / max) * (h - pad * 2);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const path = `M ${points.join(' L ')}`;
  const areaPath = `${path} L ${pad + (values.length - 1) * step},${h} L ${pad},${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-6 overflow-visible">
      <defs>
        <linearGradient id="sparkFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(243,156,18,0.35)" />
          <stop offset="100%" stopColor="rgba(243,156,18,0)" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#sparkFill)" />
      <path d={path} fill="none" stroke="#f39c12" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pad + (values.length - 1) * step} cy={h - pad - (values[values.length - 1] / max) * (h - pad * 2)} r="1.8" fill="#f39c12" stroke="#fff" strokeWidth="0.8" />
    </svg>
  );
};

// ============================================================================
// ServiceCard
// ============================================================================

type PillTone = 'emerald' | 'rose' | 'amber' | 'slate' | 'teal';

const ServiceCard: React.FC<{
  title: string;
  glyph: React.FC<any>;
  accentClass: string;
  accentChip: string;
  accentRing: string;
  total: number;
  statusPills: Array<{ label: string; count: number; tone: PillTone }>;
  empty: string;
  cta: { label: string; to: string };
  delay?: number;
  children?: React.ReactNode;
}> = ({ title, glyph: Glyph, accentClass, accentChip, accentRing, total, statusPills, empty, cta, delay, children }) => (
  <div className="fade-up" style={{ animationDelay: `${delay || 0}ms` }}>
    <div className="glass-card p-6 relative overflow-hidden">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-11 h-11 rounded-2xl ${accentChip} ring-1 ring-inset ${accentRing} ${accentClass} shadow-sm`}>
            <Glyph className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div>
            <div className="font-semibold text-slate-900">{title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{total} total</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 justify-end">
          {statusPills.filter(p => p.count > 0).map(p => (
            <Pill key={p.label} label={`${p.count} ${p.label}`} tone={p.tone} />
          ))}
        </div>
      </div>

      {total === 0 ? (
        <p className="text-sm text-slate-500 py-3">{empty}</p>
      ) : (
        <ul className="divide-y divide-slate-200/60">{children}</ul>
      )}

      <div className="mt-4 pt-3 border-t border-slate-200/60">
        <Link to={cta.to} className={`text-sm font-medium ${accentClass} hover:opacity-80 transition`}>
          {cta.label} →
        </Link>
      </div>
    </div>
  </div>
);

const Row: React.FC<{
  primary: string;
  meta: Array<{ text: string; tone?: PillTone; dim?: boolean; mono?: boolean }>;
  pulse?: boolean;
  to?: string;
}> = ({ primary, meta, pulse, to }) => {
  const body = (
    <div className="flex items-center justify-between py-2.5 group">
      <div className="flex items-center gap-2 min-w-0 mr-3">
        {pulse && (
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.6)] flex-shrink-0" />
        )}
        <span className="text-sm font-medium text-slate-900 truncate group-hover:text-orange-600 transition">{primary}</span>
      </div>
      <div className="flex items-center gap-2 text-xs whitespace-nowrap">
        {meta.map((m, i) => (
          <span key={i} className={`${
            m.tone === 'emerald' ? 'text-emerald-700' :
            m.tone === 'rose' ? 'text-rose-700' :
            m.tone === 'amber' ? 'text-amber-700' :
            m.tone === 'teal' ? 'text-teal-700' :
            m.dim ? 'text-slate-500' : 'text-slate-700'
          } ${m.mono ? 'font-mono' : ''}`}>
            {i > 0 && <span className="text-slate-300 mr-2">·</span>}
            {m.text}
          </span>
        ))}
      </div>
    </div>
  );
  return to ? <li><Link to={to} className="block">{body}</Link></li> : <li>{body}</li>;
};

const Pill: React.FC<{ label: string; tone: PillTone }> = ({ label, tone }) => {
  const map: Record<PillTone, string> = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rose:    'bg-rose-50 text-rose-700 border-rose-200',
    amber:   'bg-amber-50 text-amber-800 border-amber-200',
    slate:   'bg-slate-50 text-slate-700 border-slate-200',
    teal:    'bg-teal-50 text-teal-700 border-teal-200',
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md border ${map[tone]}`}>{label}</span>;
};

// ============================================================================
// Icons (inline SVG, no deps)
// ============================================================================

const IconHosting: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="6" rx="2" />
    <rect x="3" y="14" width="18" height="6" rx="2" />
    <circle cx="7" cy="7" r="0.8" fill="currentColor" />
    <circle cx="7" cy="17" r="0.8" fill="currentColor" />
  </svg>
);
const IconVps: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M7 8h10M7 12h10M7 16h6" />
  </svg>
);
const IconMail: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);
const IconGlobe: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
  </svg>
);

// ============================================================================
// Activity
// ============================================================================

const ActivityGlyph: React.FC<{ type: ActivityItem['type'] }> = ({ type }) => {
  const map: Record<string, React.ReactNode> = {
    order: <span className="text-sky-600 font-semibold">↗</span>,
    ticket: <span className="text-violet-600">✦</span>,
    invoice: <span className="text-amber-600 font-semibold">$</span>,
  };
  return (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/70 border border-slate-200/70 text-sm flex-shrink-0 shadow-sm">
      {map[type] || '·'}
    </span>
  );
};

const ActivityStatus: React.FC<{ s: string }> = ({ s }) => {
  const tone =
    ['paid', 'completed', 'resolved'].includes(s) ? 'emerald' :
    ['open', 'sent', 'in_progress'].includes(s) ? 'amber' :
    ['failed', 'overdue'].includes(s) ? 'rose' :
    ['cancelled', 'closed'].includes(s) ? 'slate' :
    'slate';
  return <Pill label={s} tone={tone as PillTone} />;
};

// ============================================================================
// Skeleton, empty states
// ============================================================================

const SkeletonGrid: React.FC = () => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
    {[0, 1, 2, 3].map(i => (
      <div key={i} className="glass-card p-6 fade-up" style={{ animationDelay: `${340 + i * 60}ms` }}>
        <div className="h-4 w-24 rounded bg-slate-200/70 mb-3" />
        <div className="h-8 w-32 rounded bg-slate-300/70 mb-2" />
        <div className="h-3 w-48 rounded bg-slate-200/70" />
      </div>
    ))}
  </div>
);

const FullScreenSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
  </div>
);

const AccessDenied: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-white text-slate-900">
    <div className="text-center">
      <h2 className="text-2xl font-bold mb-2">Access denied</h2>
      <p className="text-slate-500">Please log in to access your dashboard.</p>
    </div>
  </div>
);

// ============================================================================
// Helpers
// ============================================================================

function greetingFor(d: Date): string {
  const h = d.getHours();
  if (h < 5) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}
function fmtShortDate(s: string): string {
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function daysUntil(s: string): string {
  const d = Math.ceil((new Date(s).getTime() - Date.now()) / 86400_000);
  if (d <= 0) return 'today';
  if (d === 1) return 'in 1 day';
  return `in ${d} days`;
}
function fmtRelative(s: string): string {
  const diff = Date.now() - new Date(s).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 7 * 86400_000) return `${Math.floor(diff / 86400_000)}d ago`;
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function labelForActivity(a: ActivityItem): string {
  switch (a.type) {
    case 'order':   return `Order ${a.summary}`;
    case 'invoice': return `Invoice ${a.summary}`;
    case 'ticket':  return a.summary;
    default:        return a.summary;
  }
}


export default Dashboard;
